import { useEffect } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { LoadingSpinner, InlineError, FormField, ErrorBoundary } from '../components/ui';

const LoginContainer = styled.div`
  min-height: calc(100vh - var(--site-header-offset));
  min-height: calc(100dvh - var(--site-header-offset));
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xl};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}10 0%, ${({ theme }) => theme.colors.secondary}10 100%);
  
  /* Safe area padding for mobile devices */
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding-left: max(${({ theme }) => theme.spacing.xl}, env(safe-area-inset-left));
    padding-right: max(${({ theme }) => theme.spacing.xl}, env(safe-area-inset-right));
    padding-bottom: max(${({ theme }) => theme.spacing.xl}, env(safe-area-inset-bottom));
  }
`;

const LoginCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing['3xl']};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  width: 100%;
  max-width: 450px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};
`;

const Logo = styled.div`
  font-size: 48px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const LoginTitle = styled.h1`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
`;

const LoginSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.gray.dark};
  margin-bottom: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const LoginButton = styled.button<{ $loading?: boolean }>`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  width: 100%;
  
  /* Mobile-specific optimizations */
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-height: 56px; /* Larger touch target on mobile */
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
    padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.xl};
  }

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.secondary};
    transform: translateY(-1px);
  }
  
  /* Touch feedback on mobile */
  &:active:not(:disabled) {
    transform: scale(0.98);
    background-color: ${({ theme }) => theme.colors.secondary};
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
  
  /* Prevent tap highlight on mobile */
  -webkit-tap-highlight-color: transparent;
`;

const Footer = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.xl};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.gray.light};
  color: ${({ theme }) => theme.colors.gray.dark};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const schema = yup.object({
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  password: yup
    .string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
});

interface LoginFormData {
  email: string;
  password: string;
}

export function AdminLoginPage() {
  const { login, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const returnTo = searchParams.get('returnTo') || '/admin';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setFocus,
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  useEffect(() => {
    setFocus('email');
  }, [setFocus]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      clearError();
      await login(data.email, data.password);
      navigate(returnTo, { replace: true });
    } catch (err) {
      // Error is handled by auth context
      console.error('Login failed:', err);
    }
  };

  const isFormLoading = isLoading || isSubmitting;

  return (
    <ErrorBoundary>
    <LoginContainer>
      <LoginCard>
        <Header>
          <Logo>🎨</Logo>
          <LoginTitle>Teacher Portal</LoginTitle>
          <LoginSubtitle>
            Sign in to manage student artwork and exhibitions
          </LoginSubtitle>
        </Header>

        {error && (
          <InlineError error={error} onDismiss={clearError} />
        )}

        <Form onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormField
            name="email"
            label="Email Address"
            type="email"
            inputMode="email"
            placeholder="teacher@example.com"
            register={register}
            errors={errors}
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
          />

          <FormField
            name="password"
            label="Password"
            type="password"
            placeholder="Enter your password"
            register={register}
            errors={errors}
            autoComplete="current-password"
            autoCapitalize="none"
            autoCorrect="off"
          />

          <LoginButton
            type="submit"
            disabled={isFormLoading}
            $loading={isFormLoading}
          >
            {isFormLoading ? (
              <LoadingSpinner size="sm" color="white" />
            ) : (
              'Sign In'
            )}
          </LoginButton>
        </Form>

        <Footer>
          <p>
            Need help with account access? Contact your studio administrator.
          </p>
        </Footer>
      </LoginCard>
    </LoginContainer>
    </ErrorBoundary>
  );
}
